import { defineStore } from "pinia";
import axios from "axios";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import CryptoJs from "crypto-js";
import utc from "dayjs/plugin/utc";
import type { StockInfo } from "../utils/types";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("en");
dayjs.tz.setDefault("America/New_York");

type ReferencedStocksInfo = {
	[key: string]: StockInfo;
};

type StockFilters = ("most_active" | "top_gainers" | "top_losers")[];

type StockData = {
	SYMBOL: string;
	NAME: string;
	PC_CHG: number;
	NET_CHG: number;
	BID: number;
};

// Global error tracking sets
const loggedHistoricalErrorSymbols = new Set<string>();
const loggedYtdErrorSymbols = new Set<string>();
const loggedMalformedResponseSymbols = new Set<string>();

interface StockStoreState {
	stocks: ReferencedStocksInfo;
	mostActiveStocks: any[];
	topGainerStocks: any[];
	topLoserStocks: any[];
	didFetchYTD: boolean;
	activeSymbols: string[];
	serverUrls: { [key: string]: string };
	useExternalSecretToken?: string;
	fetchHistoricalData: boolean;

	// Interval management
	intervals: {
		stocksInterval?: NodeJS.Timeout;
		mostActiveInterval?: NodeJS.Timeout;
		topGainerInterval?: NodeJS.Timeout;
		topLoserInterval?: NodeJS.Timeout;
	};

	// Active filters
	activeFilters: StockFilters;
}

export const useStocksStore = defineStore("stocks", {
	state: (): StockStoreState => ({
		stocks: {},
		mostActiveStocks: [],
		topGainerStocks: [],
		topLoserStocks: [],
		didFetchYTD: false,
		activeSymbols: [],
		serverUrls: {},
		useExternalSecretToken: undefined,
		fetchHistoricalData: false,
		intervals: {},
		activeFilters: []
	}),

	getters: {
		getStockBySymbol: (state) => (symbol: string) => state.stocks[symbol],
		hasActiveIntervals: (state) =>
			Object.keys(state.intervals).some(
				(key) => state.intervals[key as keyof typeof state.intervals] !== undefined
			),
		stockCount: (state) => Object.keys(state.stocks).length
	},

	actions: {
		// Initialize store with configuration
		initialize(config: {
			serverUrls: { [key: string]: string };
			useExternalSecretToken?: string;
			fetchHistoricalData?: boolean;
		}) {
			this.serverUrls = config.serverUrls;
			this.useExternalSecretToken = config.useExternalSecretToken;
			this.fetchHistoricalData = config.fetchHistoricalData || false;
		},

		// Utility functions
		createSecretToken(): string {
			const date = dayjs
				.tz(Date.now(), "America/New_York")
				.format("ddd, D MMM YYYY");
			return CryptoJs.MD5(date).toString();
		},

		createHeaders() {
			const secretToken = this.createSecretToken();
			return {
				secretToken: this.useExternalSecretToken || secretToken,
				Accept: "application/json"
			};
		},

		// Historical data fetching
		async getHistoricalData(symbol: string, range: string = "15D") {
			try {
				const date = dayjs(Date.now()).format("ddd, D MMM YYYY");
				const secretToken = CryptoJs.MD5(date).toString();

				const headers = {
					secretToken,
					Accept: "application/json"
				};

				const end = dayjs().format("YYYY-MM-DD");
				let start;

				switch (range) {
					case "1D":
						start = dayjs().subtract(1, "day").format("YYYY-MM-DD");
						break;
					case "5D":
						start = dayjs().subtract(5, "day").format("YYYY-MM-DD");
						break;
					case "1M":
						start = dayjs().subtract(1, "month").format("YYYY-MM-DD");
						break;
					case "6M":
						start = dayjs().subtract(6, "month").format("YYYY-MM-DD");
						break;
					case "1Y":
						start = dayjs().subtract(1, "year").format("YYYY-MM-DD");
						break;
					case "5Y":
						start = dayjs().subtract(5, "year").format("YYYY-MM-DD");
						break;
					case "ALL":
						start = "1900-01-01";
						break;
					case "15D":
					default:
						start = dayjs().subtract(15, "day").format("YYYY-MM-DD");
				}

				if (dayjs(end).isAfter(dayjs())) {
					console.warn("[fetchHistoricalData] End date is in the future", {
						symbol,
						end
					});
					return null;
				}

				const endDay = dayjs(end).day();
				if ([6, 0].includes(endDay)) {
					console.warn("[fetchHistoricalData] End date is a weekend", {
						symbol,
						end,
						endDay
					});
				}

				const response = await axios.get(
					this.serverUrls["/proxyServerAddress"] + "?originReq=stockstotrade",
					{
						headers,
						params: {
							mtd: "getEnhancedChartData.json",
							start,
							end,
							symbol,
							method: "proxy",
							dataType: "eod",
							secret: secretToken,
							volume: true,
							freq: "day"
						},
						timeout: 15000
					}
				);

				if (!response.data || typeof response.data !== "object") {
					if (!loggedMalformedResponseSymbols.has(symbol)) {
						console.error(
							`[Historical] Malformed or non-JSON response for symbol: ${symbol}`,
							response.data
						);
						loggedMalformedResponseSymbols.add(symbol);
					}
					return null;
				}

				if (response.data?.results) {
					return response.data.results.history[0].eoddata;
				} else {
					return [];
				}
			} catch (error) {
				console.error(`[fetchHistoricalData] ERROR for symbol: ${symbol}`, error);
				if (!loggedHistoricalErrorSymbols.has(symbol)) {
					loggedHistoricalErrorSymbols.add(symbol);
				}
				return null;
			}
		},

		// Fetch stock data
		async fetchStocksData(list: string, headers: any) {
			try {
				const data = {
					filter: `SYMBOL IN (${list})`,
					limit: 1,
					fields:
						"BID,PE_RATIO, VOLUME, TRADES, FLOAT, SHORT_INTEREST,SHORT_INTEREST_RATIO, SYMBOL, NAME, LAST,LAST_TRADE_TIME, NET_CHG, PC_CHG, MARKET_CAP, HIGH_52W, LOW_52W, LOW, HIGH, DAY_RANGE, MARKET, YTD_CHG, YTD_PCT",
					prevDay: dayjs
						.tz(Date.now(), "America/New_York")
						.isBefore(
							dayjs.tz(Date.now(), "America/New_York").hour(8).minute(0).second(0)
						)
				};

				const res = await axios.post(
					this.serverUrls["/httpQueryBasedFilterServer"] +
						"?originReq=stockstotrade",
					data,
					{
						headers
					}
				);

				return res;
			} catch (error) {
				console.error("Error fetching stocks data:", error);
				return { data: [] };
			}
		},

		// Main task to fetch stock info
		async taskFetchInfo(newSymbols: string[]) {
			if (!newSymbols || newSymbols.length === 0) return;

			const headers = this.createHeaders();
			const list = newSymbols.map((symbol) => `'${symbol}'`).join(",");

			const res = await this.fetchStocksData(list, headers);

			if (res.data.length > 0) {
				const payload: ReferencedStocksInfo = {};

				let stock_data: StockInfo[] = res.data.map((item: any) => {
					const YTD_CHG = this.stocks[item.SYMBOL]?.YTD_CHG || 0;
					const YTD_PCT = this.stocks[item.SYMBOL]?.YTD_PCT || 0;
					const HISTORIC_DATA = this.stocks[item.SYMBOL]?.HISTORIC_DATA || null;
					const prices = HISTORIC_DATA?.map((data: any) => data.close) || [];
					const labels = HISTORIC_DATA?.map((data: any) => data.date) || [];

					return {
						...item,
						symbol: item.SYMBOL,
						HISTORIC_DATA,
						prices,
						labels,
						YTD_CHG,
						YTD_PCT
					};
				});

				if (this.fetchHistoricalData) {
					const historicalDataPromises = res.data.map(async (item: any) => {
						const symbol = item.SYMBOL;
						const HISTORIC_DATA = await this.getHistoricalData(symbol);
						const prices = HISTORIC_DATA?.map((data: any) => data.close) || [];
						const labels = HISTORIC_DATA?.map((data: any) => data.date) || [];

						return { symbol, item, HISTORIC_DATA, prices, labels };
					});

					stock_data = await Promise.all(historicalDataPromises);
				}

				stock_data.forEach((item: StockInfo) => {
					payload[item.symbol] = { ...item };
				});

				this.stocks = { ...payload };
			}
		},

		// Filtered stock fetching methods
		async fetchMostActiveStocks() {
			const headers = this.createHeaders();

			const data = {
				filter:
					"VOLUME > 5000000 AND MARKET_CAP > 2000000000 AND MARKET IN ('NGS', 'NYE', 'AMX') ORDER BY VOLUME DESC LIMIT 20",
				fields: "SYMBOL,NAME,PC_CHG,NET_CHG, BID",
				prevDay: dayjs
					.tz(Date.now(), "America/New_York")
					.isBefore(
						dayjs.tz(Date.now(), "America/New_York").hour(8).minute(0).second(0)
					)
			};

			const response = await axios.post(
				this.serverUrls["/httpQueryBasedFilterServer"],
				data,
				{
					headers
				}
			);

			return response.data;
		},

		async fetchTopGainerStocks() {
			const headers = this.createHeaders();

			const data = {
				filter:
					"VOLUME > 5000000 AND MARKET_CAP > 2000000000 AND MARKET IN ('NGS', 'NYE', 'AMX') AND PC_CHG > 0 ORDER BY PC_CHG DESC LIMIT 20",
				fields: "SYMBOL,NAME,PC_CHG,NET_CHG,BID",
				prevDay: dayjs().isBefore(dayjs().hour(8).minute(0).second(0))
			};

			const response = await axios.post(
				this.serverUrls["/httpQueryBasedFilterServer"],
				data,
				{
					headers
				}
			);

			return response.data;
		},

		async fetchTopLoserStocks() {
			const headers = this.createHeaders();

			const data = {
				filter:
					"PC_CHG < -2.5 AND MARKET_CAP > 1000000000 AND MARKET_CAP < 10000000000000 AND VOLUME > 20000 AND MARKET IN ('NGS', 'NYE', 'AMX') ORDER BY PC_CHG ASC LIMIT 20",
				fields: "SYMBOL,NAME,PC_CHG,NET_CHG,BID",
				prevDay: dayjs().isBefore(dayjs().hour(8).minute(0).second(0))
			};

			const response = await axios.post(
				this.serverUrls["/httpQueryBasedFilterServer"],
				data,
				{
					headers
				}
			);

			return response.data;
		},

		async fetchStocksBySector(sector: string) {
			const headers = this.createHeaders();

			const data = {
				filter: `SECTOR = '${sector}' AND VOLUME > 20000 AND MARKET_CAP > 1000000000 ORDER BY ABS(PC_CHG) DESC LIMIT 10`,
				fields: "SYMBOL,NAME,PC_CHG,NET_CHG,BID,SECTOR",
				prevDay: dayjs
					.tz(Date.now(), "America/New_York")
					.isBefore(
						dayjs.tz(Date.now(), "America/New_York").hour(8).minute(0).second(0)
					)
			};

			const response = await axios.post(
				this.serverUrls["/httpQueryBasedFilterServer"],
				data,
				{
					headers
				}
			);

			return response.data;
		},

		// YTD data fetching for all stocks
		async fetchYtdDataForStocks() {
			if (this.didFetchYTD) return;

			const symbols = Object.keys(this.stocks);
			if (symbols.length === 0) return;

			try {
				const ytdData = await fetch(
					`/api/stock-historic?symbols=${symbols.join(",")}&ytd=true`
				);
				if (ytdData.ok) {
					const data = await ytdData.json();

					const results = data.results;

					for (const symbol of symbols) {
						const stock = this.stocks[symbol];
						this.stocks[symbol] = { ...stock, ...results[symbol] };
					}
				}
			} catch (error) {
				console.error(`[StocksStore] Error fetching YTD for ${symbols}:`, error);
			}

			this.didFetchYTD = true;
		},

		// Session storage management
		loadFromSessionStorage() {
			try {
				const storedMostActive = sessionStorage.getItem("mostActive");
				if (storedMostActive) {
					this.mostActiveStocks = JSON.parse(storedMostActive);
				}

				const storedTopGainers = sessionStorage.getItem("topGainers");
				if (storedTopGainers) {
					this.topGainerStocks = JSON.parse(storedTopGainers);
				}

				const storedTopLosers = sessionStorage.getItem("topLosers");
				if (storedTopLosers) {
					this.topLoserStocks = JSON.parse(storedTopLosers);
				}
			} catch (error) {
				console.error("[StocksStore] Error loading from session storage:", error);
			}
		},

		// Start tracking symbols with intervals
		async startTrackingSymbols(symbols: string[]) {
			// Clean up any existing intervals
			this.stopTrackingSymbols();

			this.activeSymbols = [...symbols];

			if (symbols.length === 0) return;

			// Initial fetch
			await this.taskFetchInfo(symbols);

			// Set up interval for stock updates
			this.intervals.stocksInterval = setInterval(async () => {
				try {
					await this.taskFetchInfo(this.activeSymbols);
				} catch (error) {
					console.error("[StocksStore] Interval error:", error);
					this.stopTrackingSymbols();
				}
			}, 10000);
		},

		// Start tracking filtered stocks
		async startTrackingFilters(filters: StockFilters) {
			// Stop existing filter intervals
			this.stopTrackingFilters();
			this.activeFilters = [...filters];

			// Load from session storage first
			this.loadFromSessionStorage();

			// Most Active
			if (filters.includes("most_active")) {
				await this.updateMostActiveStocks();
				this.intervals.mostActiveInterval = setInterval(async () => {
					try {
						await this.updateMostActiveStocks();
					} catch (error) {
						console.error("[StocksStore] Most active interval error:", error);
					}
				}, 30000); // 30 seconds for filtered data
			}

			// Top Gainers
			if (filters.includes("top_gainers")) {
				await this.updateTopGainerStocks();
				this.intervals.topGainerInterval = setInterval(async () => {
					try {
						await this.updateTopGainerStocks();
					} catch (error) {
						console.error("[StocksStore] Top gainers interval error:", error);
					}
				}, 30000);
			}

			// Top Losers
			if (filters.includes("top_losers")) {
				await this.updateTopLoserStocks();
				this.intervals.topLoserInterval = setInterval(async () => {
					try {
						await this.updateTopLoserStocks();
					} catch (error) {
						console.error("[StocksStore] Top losers interval error:", error);
					}
				}, 30000);
			}
		},

		async updateMostActiveStocks() {
			try {
				const stock_data = await this.fetchMostActiveStocks();
				if (stock_data) {
					const promises = stock_data.map(async (stock: StockData) => {
						const data = await this.getHistoricalData(stock.SYMBOL, "5D");
						return {
							...stock,
							HISTORIC_DATA: data
						};
					});

					const historic_data = await Promise.all(promises);
					sessionStorage.setItem("mostActive", JSON.stringify(historic_data));

					// Apply test data transformation (as in original)
					const test_data = historic_data.map((item) => ({
						...item,
						BID: item.BID * (Math.random() * 2 + 0.5)
					}));

					this.mostActiveStocks = test_data;
				}
			} catch (error) {
				console.error("[StocksStore] Error updating most active stocks:", error);
			}
		},

		async updateTopGainerStocks() {
			try {
				const stock_data = await this.fetchTopGainerStocks();
				if (stock_data) {
					const promises = stock_data.map(async (stock: StockData) => {
						const data = await this.getHistoricalData(stock.SYMBOL, "5D");
						return {
							...stock,
							HISTORIC_DATA: data
						};
					});

					const historic_data = await Promise.all(promises);
					sessionStorage.setItem("topGainers", JSON.stringify(historic_data));
					this.topGainerStocks = historic_data;
				}
			} catch (error) {
				console.error("[StocksStore] Error updating top gainer stocks:", error);
			}
		},

		async updateTopLoserStocks() {
			try {
				const stock_data = await this.fetchTopLoserStocks();
				if (stock_data) {
					const promises = stock_data.map(async (stock: StockData) => {
						const data = await this.getHistoricalData(stock.SYMBOL, "5D");
						return {
							...stock,
							HISTORIC_DATA: data
						};
					});

					const historic_data = await Promise.all(promises);
					sessionStorage.setItem("topLosers", JSON.stringify(historic_data));
					this.topLoserStocks = historic_data;
				}
			} catch (error) {
				console.error("[StocksStore] Error updating top loser stocks:", error);
			}
		},

		// Critical: Stop tracking symbols and clean up intervals
		stopTrackingSymbols() {
			if (this.intervals.stocksInterval) {
				clearInterval(this.intervals.stocksInterval);
				this.intervals.stocksInterval = undefined;
			}

			this.activeSymbols = [];
			this.didFetchYTD = false;
		},

		// Stop tracking filtered stocks
		stopTrackingFilters() {
			if (this.intervals.mostActiveInterval) {
				clearInterval(this.intervals.mostActiveInterval);
				this.intervals.mostActiveInterval = undefined;
			}

			if (this.intervals.topGainerInterval) {
				clearInterval(this.intervals.topGainerInterval);
				this.intervals.topGainerInterval = undefined;
			}

			if (this.intervals.topLoserInterval) {
				clearInterval(this.intervals.topLoserInterval);
				this.intervals.topLoserInterval = undefined;
			}

			this.activeFilters = [];
		},

		// Complete cleanup - call this when component unmounts or route changes
		cleanup() {
			this.stopTrackingSymbols();
			this.stopTrackingFilters();

			// Optionally clear the stocks data
			// this.stocks = {}
			// this.mostActiveStocks = []
			// this.topGainerStocks = []
			// this.topLoserStocks = []
		},

		// Reset store state
		reset() {
			this.cleanup();
			this.stocks = {};
			this.mostActiveStocks = [];
			this.topGainerStocks = [];
			this.topLoserStocks = [];
			this.didFetchYTD = false;
			this.activeSymbols = [];
			this.activeFilters = [];
		}
	}
});
